import { useState, useCallback } from 'react'
import { format, subDays } from 'date-fns'
import { DateRange } from 'react-day-picker'

interface OpenAIProject {
  id: string
  name: string
  status: string
}

interface OpenAIUsageData {
  completions: {
    input_tokens: number
    output_tokens: number
    requests: number
    byModel: Record<string, { input: number; output: number; requests: number }>
  } | null
  costs: {
    total: number
    byLineItem: Record<string, number>
    byDay: { date: string; amount: number }[]
  } | null
  loading: boolean
  error: string | null
  dateRange: { from: Date; to: Date } | null
  projectId: string | null
}

export function useOpenAIUsage() {
  const [adminKey, setAdminKey] = useState<string>('')
  const [projects, setProjects] = useState<OpenAIProject[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all')
  const [usageData, setUsageData] = useState<OpenAIUsageData>({
    completions: null,
    costs: null,
    loading: false,
    error: null,
    dateRange: null,
    projectId: null
  })

  // Fetch OpenAI projects
  const fetchProjects = useCallback(async (openaiAdminKey: string) => {
    if (!openaiAdminKey.trim()) return

    try {
      const response = await fetch(
        'https://api.openai.com/v1/organization/projects?limit=100',
        {
          headers: {
            'Authorization': `Bearer ${openaiAdminKey.trim()}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        const activeProjects = (data.data || [])
          .filter((p: any) => p.status === 'active')
          .map((p: any) => ({ id: p.id, name: p.name, status: p.status }))
        setProjects(activeProjects)
      } else {
        console.error('Failed to fetch OpenAI projects:', response.status)
      }
    } catch (error) {
      console.error('Error fetching OpenAI projects:', error)
    }
  }, [])

  // Fetch OpenAI usage stats
  const fetchUsageData = useCallback(async (
    openaiAdminKey: string,
    dateRange: DateRange,
    projectId: string = 'all'
  ) => {
    if (!openaiAdminKey.trim()) {
      setUsageData(prev => ({ ...prev, error: 'Please enter an OpenAI Admin API key' }))
      return
    }

    setUsageData(prev => ({ ...prev, loading: true, error: null }))

    // Also fetch projects if we haven't yet
    if (projects.length === 0) {
      await fetchProjects(openaiAdminKey)
    }

    try {
      // Use the provided date range or default
      const usageFromDate = dateRange.from || subDays(new Date(), 29)
      const usageToDate = dateRange.to || new Date()
      const startTime = Math.floor(usageFromDate.getTime() / 1000)
      const endTime = Math.floor(usageToDate.getTime() / 1000)
      
      // Project filter
      const projectFilter = projectId !== 'all' ? `&project_ids[]=${projectId}` : ''
      
      // Calculate number of days for the limit parameter
      const daysDiff = Math.ceil((usageToDate.getTime() - usageFromDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

      // Fetch completions usage grouped by model
      const completionsResponse = await fetch(
        `https://api.openai.com/v1/organization/usage/completions?start_time=${startTime}&end_time=${endTime}&group_by=model&limit=${daysDiff}${projectFilter}`,
        {
          headers: {
            'Authorization': `Bearer ${openaiAdminKey.trim()}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!completionsResponse.ok) {
        const errorText = await completionsResponse.text()
        throw new Error(`OpenAI API error: ${completionsResponse.status} - ${errorText}`)
      }

      const completionsData = await completionsResponse.json()

      // Aggregate completions data
      let totalInputTokens = 0
      let totalOutputTokens = 0
      let totalRequests = 0
      const byModel: Record<string, { input: number; output: number; requests: number }> = {}

      completionsData.data?.forEach((bucket: any) => {
        bucket.results?.forEach((result: any) => {
          totalInputTokens += result.input_tokens || 0
          totalOutputTokens += result.output_tokens || 0
          totalRequests += result.num_model_requests || 0

          if (result.model) {
            if (!byModel[result.model]) {
              byModel[result.model] = { input: 0, output: 0, requests: 0 }
            }
            byModel[result.model].input += result.input_tokens || 0
            byModel[result.model].output += result.output_tokens || 0
            byModel[result.model].requests += result.num_model_requests || 0
          }
        })
      })

      // Fetch costs grouped by line item (limit capped at 180 per API docs)
      const costsLimit = Math.min(daysDiff, 180)
      const costsResponse = await fetch(
        `https://api.openai.com/v1/organization/costs?start_time=${startTime}&end_time=${endTime}&bucket_width=1d&group_by=line_item&limit=${costsLimit}${projectFilter}`,
        {
          headers: {
            'Authorization': `Bearer ${openaiAdminKey.trim()}`,
            'Content-Type': 'application/json'
          }
        }
      )

      let totalCost = 0
      const byLineItem: Record<string, number> = {}
      const dailyCosts: Record<string, number> = {}

      if (costsResponse.ok) {
        const costsData = await costsResponse.json()
        if (costsData.data && Array.isArray(costsData.data)) {
          costsData.data.forEach((bucket: any) => {
            const bucketDate = bucket.start_time ? format(new Date(bucket.start_time * 1000), 'yyyy-MM-dd') : null
            if (bucket.results && Array.isArray(bucket.results)) {
              bucket.results.forEach((result: any) => {
                const rawValue = result.amount?.value
                const amount = typeof rawValue === 'string' ? parseFloat(rawValue) : (rawValue ?? 0)
                totalCost += amount
                
                // Aggregate by line item (line_item can be null when not grouped)
                const lineItemKey = result.line_item || 'total'
                byLineItem[lineItemKey] = (byLineItem[lineItemKey] || 0) + amount
                
                // Aggregate by day
                if (bucketDate) {
                  dailyCosts[bucketDate] = (dailyCosts[bucketDate] || 0) + amount
                }
              })
            }
          })
        }
        
      } else {
        const errorText = await costsResponse.text()
        console.error('Costs API response not OK:', costsResponse.status, errorText)
      }

      // Convert daily costs to sorted array
      const byDay = Object.entries(dailyCosts)
        .map(([date, amount]) => ({ date, amount }))
        .sort((a, b) => a.date.localeCompare(b.date))

      setUsageData({
        completions: {
          input_tokens: totalInputTokens,
          output_tokens: totalOutputTokens,
          requests: totalRequests,
          byModel
        },
        costs: {
          total: totalCost,
          byLineItem,
          byDay
        },
        loading: false,
        error: null,
        dateRange: { from: usageFromDate, to: usageToDate },
        projectId: projectId !== 'all' ? projectId : null
      })

    } catch (error) {
      console.error('Error fetching OpenAI usage:', error)
      setUsageData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch OpenAI usage'
      }))
    }
  }, [projects.length, fetchProjects])

  return {
    adminKey,
    setAdminKey,
    projects,
    selectedProjectId,
    setSelectedProjectId,
    usageData,
    fetchProjects,
    fetchUsageData
  }
}

// Re-export types for consumers
export type { OpenAIProject, OpenAIUsageData }
